import React, { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";

import { isNone } from "~/common/assert";
import { PAYERS_TRANSLATION_MAP, PAYMENT_OPTIONS_TRANSLATION_MAP, PayerId, type PaymentMethod, PaymentMethodId, type PaymentOption, PaymentOptionId } from "~/common/constants";
import { CourseId, getCapLikeCourseOptionTag, isPromoCodeAvailableForCourse, Status } from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { LabelledValue } from "~/components/ui/labelled-value";
import type { TCourseInstallment } from "~/slices/course-installment";
import { addInstructions, removeInstructions, selectInstructions } from "~/slices/instructions";
import { selectAdditionalAlignersFormValues, selectCorrectionFormValues } from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";
import type { SetDirtyFn } from "~/types/patient-new";

import { getAdditionalAlignersAmount } from "../patient_new/patient_new_additional_aligners";
import { getExpertSupportPrice } from "../patient_new/patient_new_additional_services";
import { PayerRadioGroup, PaymentMethodRadioGroup, PaymentOptionRadioGroup, PromoCodeInput } from "../patient_new/patient_new_payment";

const mapStateToProps = (state: RootState) => {
  return {
    instructions: selectInstructions(state),
    formValues: selectCorrectionFormValues(state),
    additionalAlignersFormValues: selectAdditionalAlignersFormValues(state),
  };
};

const mapDispatchToProps = {
  addInstruction: addInstructions,
  removeInstructions,
};

type PatientUpdatePaymentProps = PropsFromRedux & {
  intl: IntlShape;
  patient: TPatient;
  courseInstallment: TCourseInstallment;
  setDirty: SetDirtyFn;
};

class PatientUpdatePayment extends Component<PatientUpdatePaymentProps> {
  constructor(props: PatientUpdatePaymentProps) {
    super(props);
    this.initPaymentOption = this.initPaymentOption.bind(this);
    this.selectPaymentMethod = this.selectPaymentMethod.bind(this);
    this.selectPaymentOption = this.selectPaymentOption.bind(this);
    this.selectPayer = this.selectPayer.bind(this);
    this.setPaymentCreditOption = this.setPaymentCreditOption.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_update_payment");
  }

  componentDidMount() {
    const { patient } = this.props;

    if (!isNone(patient.course.payment_method)) {
      const paymentMethodStrToId: Record<PaymentMethod, PaymentMethodId> = {
        PM_BANK: PaymentMethodId.INVOICE,
        PM_CASH: PaymentMethodId.CASH,
        PM_CARD: PaymentMethodId.CARD,
      };
      this.selectPaymentMethod(paymentMethodStrToId[patient.course.payment_method]);
    }

    if (!isNone(patient.course.payment_option)) {
      const paymentOptionStrToId: Record<PaymentOption, PaymentOptionId> = {
        PO_ADVANCE: PaymentOptionId.ADVANCED,
        PO_SPLIT_2: PaymentOptionId.SPLIT_2,
        PO_SPLIT_3: PaymentOptionId.SPLIT_3,
        PO_SPLIT_2_2: PaymentOptionId.SPLIT_2_2,
        PO_SPLIT_3_2: PaymentOptionId.SPLIT_3_2,
      };
      this.initPaymentOption(paymentOptionStrToId[patient.course.payment_option]);
    }

    if (patient.payer_id) {
      this.props.addInstruction({
        payer_id: patient.payer_id,
        payer_last_name: patient.payer_last_name || null,
        payer_first_name: patient.payer_first_name || null,
        payer_patronymic: patient.payer_patronymic || null,
      });
    }

    if (patient.sber_credit) {
      this.setPaymentCreditOption();
    }

    if (patient.promo_code) {
      this.props.addInstruction({ promo_code: patient.promo_code });
    }
  }

  setPaymentCreditOption() {
    /** Payment option should be Advanced in case if credit option is chosen. On the UI/Interface level we do not show this */
    this.props.addInstruction({ sber_credit: true, payment_option_id: PaymentOptionId.ADVANCED });
  }

  selectPaymentMethod(value: PaymentMethodId) {
    this.props.addInstruction({ payment_method_id: value });
    this.props.setDirty("payment_method_id", value);
  }

  initPaymentOption(data: PaymentOptionId) {
    this.props.addInstruction({ payment_option_id: data });
  }

  selectPaymentOption(value: PaymentOptionId) {
    const { can_edit_payment_option } = this.props.patient;

    if (can_edit_payment_option) {
      this.props.addInstruction({ payment_option_id: value, sber_credit: false });
      this.props.setDirty("payment_option_id", value);
    }
  }

  changePromoCode(value: string) {
    this.props.addInstruction({ promo_code: value });
    this.props.setDirty("promo_code", value);
  }

  selectPayer(value: PayerId) {
    this.props.addInstruction({ payer_id: value });
    this.props.setDirty("payer_id", value);

    if (value == PayerId.PATIENT) {
      if (this.props.instructions.payment_method_id != PaymentMethodId.CARD) {
        this.props.addInstruction({ payment_method_id: null });
      }
    } else if (value == PayerId.DOCTOR) {
      if (this.props.instructions.payment_method_id != PaymentMethodId.CARD) {
        this.props.addInstruction({ payment_method_id: null });
      }
      this.removePatientPayer();
    } else {
      if (this.props.instructions.payment_method_id != PaymentMethodId.INVOICE) {
        this.props.addInstruction({ payment_method_id: null });
      }
      this.removePatientPayer();
    }
  }

  removePatientPayer() {
    this.props.removeInstructions({
      email: null,
      payer_last_name: null,
      payer_first_name: null,
      payer_patronymic: null,
    });
    if (this.props.instructions.sber_credit === true) {
      this.props.removeInstructions({ payment_option_id: null });
    }
    this.props.addInstruction({ sber_credit: false });
  }

  render() {
    const {
      patient,
      instructions: { course_id, payer_id, payment_method_id, payment_option_id, sber_credit, promo_code = "", additional_service_tags },
      formValues: { arch, quantity } = {},
      additionalAlignersFormValues: { aligners = [] } = {},
      courseInstallment,
    } = this.props;

    const courseId = course_id ?? patient.course.course_id;
    const installments = !isNone(courseId) ? courseInstallment[courseId] : {};
    const additionalAlignersAmount = getAdditionalAlignersAmount(aligners);

    return (
      <div>
        <h3 id="payment-block-label" className="block" style={{ fontWeight: "900" }}>
          <FormattedMessage id="patient.form.payment.title" />
        </h3>

        {patient.status == Status.UNFILLED_CASE ? (
          <PayerRadioGroup payer_id={payer_id} onValueChange={(value) => this.selectPayer(value)} />
        ) : (
          <LabelledValue label={<FormattedMessage id="HEADER_PAYER" />}>
            <FormattedMessage id={PAYERS_TRANSLATION_MAP[patient.payer_id]} />
          </LabelledValue>
        )}

        {patient.status == Status.UNFILLED_CASE ? (
          <PaymentMethodRadioGroup payer_id={payer_id} payment_method_id={payment_method_id} onValueChange={(value) => this.selectPaymentMethod(value)} />
        ) : (
          <LabelledValue label={<FormattedMessage id="HEADER_PAYMENT_METHOD" />}>
            <FormattedMessage id={patient.course.payment_method} />
          </LabelledValue>
        )}

        {patient.can_edit_payment_option ? (
          <PaymentOptionRadioGroup
            payment_option_id={payment_option_id}
            installments={installments}
            isSberCredit={sber_credit == true}
            course_id={courseId}
            course_option_tag={getCapLikeCourseOptionTag(courseId, arch, quantity)}
            additionalAlignersAmount={additionalAlignersAmount}
            additionalServicesAmount={getExpertSupportPrice(additional_service_tags ?? patient.additional_service_tags)}
            onValueChange={(value) => this.selectPaymentOption(value)}
          />
        ) : (
          <LabelledValue className="tw-flex tw-flex-col tw-gap-3" label={<FormattedMessage id="HEADER_PAYMENT_OPTION" />}>
            {payment_option_id ? (
              <FormattedMessage
                id={
                  (payment_option_id == PaymentOptionId.SPLIT_3 || payment_option_id == PaymentOptionId.SPLIT_3_2) &&
                  (patient.course.course_id == CourseId.LIMITLESS ||
                    (patient.course.course_option_tag &&
                      (patient.course.course_option_tag == "LIMITLESS_3_STEPS_V1" || patient.course.course_option_tag == "LIMITLESS_V2" || patient.course.course_option_tag == "LIMITLESS_3_STEPS_V2")))
                    ? "PO_SPLIT_3_NO_OVERPAY"
                    : PAYMENT_OPTIONS_TRANSLATION_MAP[payment_option_id]
                }
              />
            ) : null}
          </LabelledValue>
        )}

        {isPromoCodeAvailableForCourse(courseId) ? (
          [Status.UNFILLED_CASE, Status.INSTRUCTIONS_PROCESSING].includes(patient.status) ? (
            <PromoCodeInput value={promo_code} onValueChange={(value) => this.changePromoCode(value)} />
          ) : promo_code ? (
            <LabelledValue label={<FormattedMessage id="HEADER_PROMO_CODE" />}>{promo_code}</LabelledValue>
          ) : null
        ) : null}
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientUpdatePayment));
